import { Button, Option, Select } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import { useLabelsContext } from '../contexts/LabelsContext'
import { AgamiMediaFilter, AgamiSortMode } from '../utils/helpers/agami.helpers'

interface ExperienceAgamiFiltersProps {
  typeFilter: number | null
  setTypeFilter: (value: number | null) => void
  mediaFilter: AgamiMediaFilter | null
  setMediaFilter: (value: AgamiMediaFilter | null) => void
  sortMode: AgamiSortMode
  setSortMode: (value: AgamiSortMode) => void
  darkMode?: boolean
}

const MEDIA_FILTER_VALUES: AgamiMediaFilter[] = ['audio', 'video', 'none']
const SORT_MODES: AgamiSortMode[] = ['default', 'name', 'lastUpdate']

const ALL = 'all'

/**
 * The filters of the experience view (05.3): activation type and media type,
 * applied by the container BEFORE the draft partition so both sections and
 * their counters reflect them. Extracted on day one — the container is already
 * the most crowded one of the project, and 05.6/06.4 will add more to it.
 *
 * The activation types come from the lookup (LabelsContext, unfiltered on
 * is_active), not from a hardcoded list: when a new type enters the vocabulary
 * it appears here by itself. There is deliberately NO status filter
 * (published/drafts): the two sections ARE that filter.
 */
const ExperienceAgamiFilters: React.FC<ExperienceAgamiFiltersProps> = ({
  typeFilter,
  setTypeFilter,
  mediaFilter,
  setMediaFilter,
  sortMode,
  setSortMode,
  darkMode,
}) => {
  const { t } = useTranslation()
  const { spotTypes } = useLabelsContext()

  const hasActiveFilter = typeFilter !== null || mediaFilter !== null

  const selectProps = (isActive: boolean) => {
    let colorProps = {}
    if (!isActive && darkMode) colorProps = { color: 'blue-gray' as const }

    return {
      size: 'md' as const,
      menuProps: { className: 'dark:bg-primary' },
      ...colorProps,
      className: isActive
        ? '!border-primary !border-t-transparent bg-primary/10 transition-colors duration-150 dark:!border-accent-blue-500 dark:!border-t-transparent dark:bg-accent-blue-500/15'
        : '',
      labelProps: {
        className: isActive
          ? '!text-primary before:!border-primary after:!border-primary peer-focus:!text-primary dark:!text-accent-blue-300 dark:before:!border-accent-blue-500 dark:after:!border-accent-blue-500 dark:peer-focus:!text-accent-blue-300'
          : 'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
      },
    }
  }

  return (
    <div
      className="flex flex-col gap-4 mb-4 md:flex-row md:flex-wrap md:items-center"
      data-testid="experience-agami-filters"
    >
      <div className="w-full md:w-48">
        <Select
          {...selectProps(typeFilter !== null)}
          label={t('dashboardExperienceDetail.filters.activationType')}
          value={typeFilter === null ? ALL : String(typeFilter)}
          onChange={(value) => setTypeFilter(!value || value === ALL ? null : Number(value))}
          data-testid="agami-type-filter"
        >
          {[
            <Option key={ALL} value={ALL} data-testid="agami-type-filter-option-all">
              <span className="text-primary dark:text-white">
                {t('dashboardExperienceDetail.filters.all')}
              </span>
            </Option>,
            ...spotTypes.map((spotType) => (
              <Option
                key={spotType.id}
                value={String(spotType.id)}
                data-testid={`agami-type-filter-option-${spotType.id}`}
              >
                <span className="text-primary dark:text-white uppercase">{spotType.label}</span>
              </Option>
            )),
          ]}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(mediaFilter !== null)}
          label={t('dashboardExperienceDetail.filters.mediaType')}
          value={mediaFilter === null ? ALL : mediaFilter}
          onChange={(value) => setMediaFilter(!value || value === ALL ? null : (value as AgamiMediaFilter))}
          data-testid="agami-media-filter"
        >
          {[
            <Option key={ALL} value={ALL} data-testid="agami-media-filter-option-all">
              <span className="text-primary dark:text-white">
                {t('dashboardExperienceDetail.filters.all')}
              </span>
            </Option>,
            ...MEDIA_FILTER_VALUES.map((media) => (
              <Option key={media} value={media} data-testid={`agami-media-filter-option-${media}`}>
                <span className="text-primary dark:text-white">
                  {t(`dashboardExperienceDetail.filters.${media}`)}
                </span>
              </Option>
            )),
          ]}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(sortMode !== 'default')}
          label={t('dashboardExperienceDetail.sort.label')}
          value={sortMode}
          onChange={(value) => value && setSortMode(value as AgamiSortMode)}
          aria-label={`${t('dashboardExperienceDetail.sort.label')}: ${t(
            `dashboardExperienceDetail.sort.${sortMode}`
          )}`}
          data-testid="agami-sort-select"
        >
          {SORT_MODES.map((mode) => (
            <Option key={mode} value={mode} data-testid={`agami-sort-option-${mode}`}>
              <span className="text-primary dark:text-white">
                {t(`dashboardExperienceDetail.sort.${mode}`)}
              </span>
            </Option>
          ))}
        </Select>
      </div>

      {hasActiveFilter && (
        <Button
          size="sm"
          variant="text"
          className="whitespace-nowrap underline !text-primary dark:!text-accent"
          data-testid="agami-filters-reset"
          onClick={() => {
            setTypeFilter(null)
            setMediaFilter(null)
          }}
        >
          {t('dashboardExperienceDetail.filters.reset')}
        </Button>
      )}
    </div>
  )
}

export default ExperienceAgamiFilters
